import { useCallback } from 'react'
import { AnalyticsEvent, userProperties } from '@core/analytics'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Profile } from '@core/entities/profile'
import { analytics } from 'webapp/components/init/InitSegment'
import { useNavigationContext } from './useNavigationContext'

export const shouldSendAnalytics = (profile: Profile | null) => {
  if (!profile) {
    return false
  }

  if (Constants.E2E) {
    return false
  }

  return true
}

export const useAnalytics = () => {
  const { data: profile } = useGetProfile()
  const { organisationId, projectId } = useNavigationContext()

  const identify = useCallback(
    (profileArg: Profile) => {
      if (shouldSendAnalytics(profile)) {
        const { userId, properties } = userProperties(profileArg)
        analytics.identify(userId, properties)
      }
    },
    [profile],
  )

  const page = useCallback(
    (params: { withProjectAndOrgEventProperties?: boolean } = {}) => {
      const { withProjectAndOrgEventProperties = true } = params

      if (shouldSendAnalytics(profile)) {
        const screenSize = {
          height: window.screen.height,
          width: window.screen.width,
        }

        analytics.page({
          ...(withProjectAndOrgEventProperties && {
            context_projectId: projectId,
            context_organisationId: organisationId,
          }),
          screenSize,
        })
      }
    },
    [organisationId, profile, projectId],
  )

  const sendAnalyticsEvent = useCallback(
    (
      event: AnalyticsEvent,
      params: {
        withProjectAndOrgEventProperties?: boolean
      } = {},
    ) => {
      const { withProjectAndOrgEventProperties = true } = params

      if (shouldSendAnalytics(profile)) {
        const eventProperties = {
          ...(withProjectAndOrgEventProperties && {
            context_projectId: projectId,
            context_organisationId: organisationId,
          }),
          ...(event?.eventProperties || {}),
        }
        const eventName = event.eventName
        analytics.track(eventName, eventProperties)
      }
    },
    [organisationId, profile, projectId],
  )

  return {
    identify,
    page,
    sendAnalyticsEvent,
  }
}
